import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Loader2 } from 'lucide-react';
import { FormModal } from '@/components/modal';
import type { FeedPost } from '../types';

/** Domains that block iframing entirely via X-Frame-Options / CSP */
const NON_IFRAMEABLE_HOSTS = [
  'facebook.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  'linkedin.com',
  'tiktok.com',
];

function isNonIframeable(url: string): boolean {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return NON_IFRAMEABLE_HOSTS.some((d) => host === d || host.endsWith('.' + d));
  } catch {
    return false;
  }
}

/** Returns true if the first alphabetic character is Hebrew. */
function isHebrew(text: string): boolean {
  const match = text.match(/[\p{L}]/u);
  return !!match && /[\u0590-\u05FF]/.test(match[0]);
}

/**
 * Pixels to crop from the top of iframed news sites.
 * Most news sites have a navbar (~60px) + broken/lazy hero image (~300-500px)
 * that doesn't render properly inside an iframe context.
 * This shifts the iframe up so the article headline area is visible immediately.
 */
const IFRAME_HEADER_CROP_PX = 450;

interface ArticleViewerModalProps {
  post: FeedPost | null;
  isOpen: boolean;
  onClose: () => void;
  /** Override the iframe src (e.g. a proxy URL). "Open in browser" still uses post.sourceUrl. */
  iframeUrl?: string;
  /** Raw HTML to render in iframe via srcdoc (bypasses X-Frame-Options entirely). */
  srcdocHtml?: string;
}

export function ArticleViewerModal({ post, isOpen, onClose, iframeUrl, srcdocHtml }: ArticleViewerModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const sourceUrl = post?.sourceUrl ?? '';
  const iframeSrc = iframeUrl || sourceUrl;
  const title = post?.sourceName ?? t('feed.viewArticle');
  // Social media sites refuse iframes entirely → show our own content
  const showContent = !!sourceUrl && isNonIframeable(sourceUrl);

  // Parse title/body from content (RSS stores "title\n\nbody")
  const nlIdx = post?.content.indexOf('\n\n') ?? -1;
  const postTitle = nlIdx > -1 ? post!.content.slice(0, nlIdx) : (post?.content ?? '');
  const postBody = nlIdx > -1 ? post!.content.slice(nlIdx + 2) : '';
  const contentDir = isHebrew(postTitle || postBody) ? 'rtl' : undefined;

  // RSS iframes get a header crop to skip past broken hero images
  const shouldCrop = !showContent && post?.source === 'rss';
  const cropPx = shouldCrop && !isLoading ? IFRAME_HEADER_CROP_PX : 0;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="max-w-4xl"
    >
      <div className="flex flex-col h-full -m-3 md:-m-3">
        {showContent ? (
          /* ─── Content mode: show post's own media + text ─── */
          <div className="flex-1 overflow-y-auto">
            {/* Post image — links to source */}
            {post?.mediaUrl && post.mediaType === 'image' && (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={post.mediaUrl}
                  alt={postTitle || t('feed.post_media')}
                  className="w-full object-contain max-h-[50vh] bg-black/5 cursor-pointer"
                />
              </a>
            )}

            {/* Post text */}
            <div className="px-5 py-4" dir={contentDir}>
              {postTitle && (
                <h2 className="font-bold text-lg leading-snug mb-2" style={{ color: '#050505' }}>
                  {postTitle}
                </h2>
              )}
              {postBody && (
                <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words" style={{ color: '#333' }}>
                  {postBody}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* ─── Iframe mode: embed the article ─── */
          <div className="relative flex-1 overflow-hidden" style={{ minHeight: '60vh' }}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-neutral-500 z-10">
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('feed.articleLoading')}
              </div>
            )}
            {(iframeSrc || srcdocHtml) && (
              <iframe
                {...(srcdocHtml ? { srcDoc: srcdocHtml } : { src: iframeSrc })}
                title={title}
                className="absolute inset-0 w-full border-0"
                style={{
                  top: srcdocHtml ? 0 : -cropPx,
                  height: srcdocHtml ? '100%' : `calc(100% + ${cropPx}px)`,
                }}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => setIsLoading(false)}
              />
            )}
          </div>
        )}

        {/* Bottom bar — "Open in browser" */}
        <div
          className="flex items-center justify-center px-4 py-3 flex-shrink-0"
          style={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: 'rgba(255,255,255,0.7)',
          }}
        >
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors hover:underline"
            style={{ color: '#ea580c' }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('feed.openInBrowser')}
          </a>
        </div>
      </div>
    </FormModal>
  );
}
